
import React, { useState, useEffect, useMemo } from "react";
import { ReactComponent as VerticalLine } from "../../img/verticalline.svg";
import { ReactComponent as MagnificationLogo } from "../../img/magnificationlensicon.svg";
import { instance2 } from "../../utils/axiosInstance";
import { useLocation, useNavigate } from "react-router-dom";
import { formatNumber } from "../../numberingformat";
import { Button } from "src/components/shadcn/button";
import { Input } from "src/components/shadcn/input";
// import { se } from "date-fns/locale";

export default function DaftarStock({ tombol }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const locationId = localStorage.getItem("locationId");
  const kitchenId = localStorage.getItem("kitchenId");
  const roleId = parseInt(localStorage.getItem("roleId"), 10);

  const [data, setData] = useState([]);
  const [kitchens, setKitchens] = useState([]);
  const [selectedKitchenId, setSelectedKitchenId] = useState(kitchenId || "");
  const [loading, setLoading] = useState(true);
  const [stockSearchQuery, setStockSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc" });
  const [editedStocks, setEditedStocks] = useState({}); // store formatted display string
  const [initialStocks, setInitialStocks] = useState({}); // store formatted display string
  const [currentPage, setCurrentPage] = useState(1);

  // --- helpers for parsing/formatting user input (id-style display: thousands '.' and decimal ',') ---
  /**
   * Parse user-friendly input (e.g. "1.234,56", "1,5", "1234.56", "1000") to a JS number.
   * Returns Number (float). If parse fails returns 0.
   */
  const parseNumberFromInput = (s) => {
    if (s === null || s === undefined) return 0;
    const str = String(s).trim();
    if (str === "") return 0;
    let cleaned = str.replace(/\s/g, "");

    // If both '.' and ',' present, assume '.' thousand and ',' decimal: "1.234,56"
    if (cleaned.includes(".") && cleaned.includes(",")) {
      cleaned = cleaned.replace(/\./g, "").replace(",", ".");
    } else if (cleaned.includes(",") && !cleaned.includes(".")) {
      // "1,5" -> "1.5"
      cleaned = cleaned.replace(",", ".");
    } else {
      // Only dots or only digits: remove non-digit except dot/minus.
      cleaned = cleaned.replace(/[^\d.-]/g, "");
      const parts = cleaned.split(".");
      if (parts.length > 2) {
        const last = parts.pop();
        cleaned = parts.join("") + "." + last;
      }
    }

    const n = parseFloat(cleaned);
    return Number.isFinite(n) ? n : 0;
  };

  /**
   * Format an input-like string/value for display using id-ID style:
   * - thousands separator: '.'
   * - decimal separator: ','
   * Preserves a trailing decimal separator when user is typing (e.g. "1," or ",").
   */
  const formatInputValue = (raw) => {
    raw = String(raw ?? "");
    const original = raw;
    raw = raw.replace(/\s/g, "");
    if (raw === "") return "";

    const onlyAllowed = raw.replace(/[^\d.,-]/g, "");
    const endsWithComma = original.endsWith(",");
    const endsWithDot = original.endsWith(".");

    // If comma present -> treat comma as decimal separator
    if (onlyAllowed.includes(",")) {
      const cleaned = onlyAllowed.replace(/\./g, "");
      const [intPart = "", decPart = ""] = cleaned.split(",", 2);
      const intDigits = intPart.replace(/^0+(?=\d)/, "") || "0";
      const formattedInt = intDigits.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
      if (endsWithComma) return `${formattedInt},`;
      return decPart !== "" ? `${formattedInt},${decPart}` : formattedInt;
    }

    // No comma: check dots
    if (onlyAllowed.includes(".")) {
      const lastDotIndex = onlyAllowed.lastIndexOf(".");
      const digitsAfter = onlyAllowed.length - lastDotIndex - 1;
      if (digitsAfter > 0 && digitsAfter <= 2) {
        // treat last dot as decimal
        const withMarker = onlyAllowed.replace(/\.(?=[^.]*$)/, "<<<DOT>>>");
        const removedOtherDots = withMarker.replace(/\./g, "");
        const converted = removedOtherDots.replace("<<<DOT>>>", ",");
        const [intPart = "", decPart = ""] = converted.split(",", 2);
        const intDigits = intPart.replace(/^0+(?=\d)/, "") || "0";
        const formattedInt = intDigits.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
        if (endsWithDot) return `${formattedInt},`;
        return decPart !== "" ? `${formattedInt},${decPart}` : formattedInt;
      }
      // otherwise dots are thousand separators
      const onlyDigits = onlyAllowed.replace(/\./g, "");
      const intDigits = onlyDigits.replace(/^0+(?=\d)/, "") || "0";
      return intDigits.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    }

    // only digits
    const onlyDigits = onlyAllowed.replace(/\D/g, "");
    const intDigits = onlyDigits.replace(/^0+(?=\d)/, "") || "0";
    return intDigits.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  };

  // Fetch kitchens (once, for roleId 4)
  useEffect(() => {
    if (roleId === 4) {
      instance2.get("/kitchen").then((res) => {
        setKitchens(res.data || []);
      });
    }
  }, [roleId]);

  // Fetch stock data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        let url;
        if (roleId === 2 || roleId === 5 || roleId === 4) {
          url = `/ingredients_stock/location/${locationId}`;
        } else {
          url = `/ingredients_stock/kitchen/${kitchenId}`;
        }
        const response = await instance2.get(url);

        // ensure we have array
        const respData = Array.isArray(response.data) ? response.data : [];

        setData(respData.sort((a, b) => parseNumberFromInput(String(b.stock)) - parseNumberFromInput(String(a.stock))));

        // Set initial stocks as formatted strings for display/compare
        const stockMap = {};
        respData.forEach((item) => {
          stockMap[item.ingredient_id] = formatInputValue(String(item.stock));
        });
        setInitialStocks(stockMap);
      } catch (error) {
        // Optionally handle error
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [roleId, locationId, kitchenId, selectedKitchenId]);

  // Search and sort
  const filteredSortedData = useMemo(() => {
    let filtered = data.filter((item) => {
      const matchesName = (item.ingredient_name?.toLowerCase() || "").includes(
        stockSearchQuery.toLowerCase(),
      );
      const matchesKitchenId =
        parseInt(item.kitchen_id) === parseInt(selectedKitchenId);
      if (selectedKitchenId) {
        return matchesName && matchesKitchenId;
      }
      return matchesName;
    });

    if (!sortConfig.key) return filtered;
    return [...filtered].sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key])
        return sortConfig.direction === "asc" ? -1 : 1;
      if (a[sortConfig.key] > b[sortConfig.key])
        return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [data, stockSearchQuery, sortConfig, selectedKitchenId]);

  // Pagination
  const isDashboard = pathname === "/";
  const itemsPerPage = isDashboard ? 5 : 10;
  const totalPages = Math.max(1, Math.ceil(filteredSortedData.length / itemsPerPage));
  const paginatedData = useMemo(
    () =>
      filteredSortedData.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage,
      ),
    [filteredSortedData, currentPage, itemsPerPage],
  );

  // Pagination page numbers
  const maxVisiblePages = isDashboard ? 3 : 5;
  let pageNumbers = [];
  if (totalPages <= maxVisiblePages) {
    pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);
  } else if (currentPage <= (isDashboard ? 2 : 3)) {
    pageNumbers = isDashboard
      ? [1, 2, 3, "...", totalPages]
      : [1, 2, 3, 4, 5, "...", totalPages];
  } else if (currentPage >= totalPages - 2) {
    pageNumbers = isDashboard
      ? [1, "...", totalPages - 3, totalPages - 2, totalPages - 1, totalPages]
      : [
          1,
          "...",
          totalPages - 4,
          totalPages - 3,
          totalPages - 2,
          totalPages - 1,
          totalPages,
        ];
  } else {
    pageNumbers = [
      1,
      "...",
      currentPage - 1,
      currentPage,
      currentPage + 1,
      "...",
      totalPages,
    ];
  }

  // Stock editing handlers
  const handleStockChange = (id, newValueDisplay) => {
    setEditedStocks((prev) => ({
      ...prev,
      [id]: newValueDisplay,
    }));
  };

  const handleSaveStock = async (id, originalItem) => {
    // newStockDisplay is formatted string like "1.000" or "1,5"
    const newStockDisplay = editedStocks[id];
    // fallback to initial/ original if undefined
    const displayToParse = newStockDisplay !== undefined ? newStockDisplay : formatInputValue(String(originalItem.stock));
    const qtyNumeric = parseNumberFromInput(displayToParse);

    try {
      await instance2.post(`/ingredient_stock_transaction/kitchen`, {
        admin_id: localStorage.getItem("userAuthentication"),
        ingredient_id: id,
        kitchen_id:
          selectedKitchenId || parseInt(localStorage.getItem("kitchenId")),
        date: new Date(),
        qty: qtyNumeric,
      });
      // update initial stocks (store formatted display)
      setInitialStocks((prev) => ({
        ...prev,
        [id]: formatInputValue(String(qtyNumeric)),
      }));
      setEditedStocks((prev) => {
        const newState = { ...prev };
        delete newState[id];
        return newState;
      });
      window.location.reload();
    } catch (error) {
      // Optionally handle error
    }
  };

  const handleSetAllZero = () => {
    const newEditedStocks = {};
    data.forEach((item) => {
      newEditedStocks[item.ingredient_id] = formatInputValue("0");
    });
    setEditedStocks(newEditedStocks);
  };

  const allStocksAreZero = () =>
    data.every((item) => {
      const edited = editedStocks[item.ingredient_id];
      const currentDisplay = edited !== undefined ? edited : formatInputValue(String(item.stock));
      return parseNumberFromInput(currentDisplay) === 0;
    });

  const handleSaveAllStocks = async () => {
    const updates = Object.entries(editedStocks);
    if (updates.length === 0) {
      alert("Tidak ada perubahan stok.");
      return;
    }
    try {
      const payloads = updates.map(([ingredient_id, displayValue]) => {
        const qty = parseNumberFromInput(displayValue);
        return {
          admin_id: localStorage.getItem("userAuthentication"),
          ingredient_id,
          kitchen_id: selectedKitchenId || localStorage.getItem("kitchenId"),
          date: new Date(),
          qty,
        };
      });
      await Promise.all(
        payloads.map((payload) =>
          instance2.post(`/ingredient_stock_transaction/kitchen`, payload),
        ),
      );
      alert("Semua stok berhasil diperbarui!");
      setEditedStocks({});
      window.location.reload();
    } catch (error) {
      alert("Terjadi kesalahan saat memperbarui semua stok.");
    }
  };

  const hasAnyStockChanged = Object.entries(editedStocks).some(
    ([id, newDisplay]) => {
      const initialDisplay = initialStocks[id] ?? formatInputValue("0");
      return parseNumberFromInput(newDisplay) !== parseNumberFromInput(initialDisplay);
    },
  );

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const getSortArrow = (key) =>
    sortConfig.key === key ? (sortConfig.direction === "asc" ? "▲" : "▼") : "";

  // Pagination handlers
  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };
  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  return (
    <div className="p-4">
      <div className="@container">
        <div className="flex flex-col @xl:flex-row items-center justify-between mb-1 h-full gap-2">
          <h2 className="text-xl sm:text-2xl font-medium text-balance text-left w-full">
            Stok Bahan Baku
          </h2>
          <div
            className={`grid grid-cols-1 sm:grid-cols-2 md:grid-flow-col gap-2 ${tombol ? "w-full" : ""}`}
          >
            {roleId === 3 && tombol && (
              <Button
                className="w-full"
                onClick={() => navigate("/daftarStock")}
              >
                Simpan
              </Button>
            )}
            {roleId === 3 && !allStocksAreZero() && (
              <Button className="w-full m-4" onClick={handleSetAllZero}>
                Kosongkan Stok
              </Button>
            )}
            {roleId === 3 && hasAnyStockChanged && (
              <Button className="w-full m-4" onClick={handleSaveAllStocks}>
                Simpan Semua
              </Button>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex w-full items-center justify-center py-6 text-gray-500">
          Loading...
        </div>
      ) : data.length === 0 ? (
        <div className="flex w-full items-center justify-center sm:text-xs md:text-xs lg:text-base text-center py-6 text-gray-500">
          Tidak ada data yang tersedia
        </div>
      ) : (
        <>
          <div className="flex flex-row @xl:flex-row items-center w-full gap-1">
            {/* Kitchen filter select for role 4 */}
            {roleId === 4 && (
              <div className="mb-4 flex items-center gap-2">
                <label htmlFor="kitchen-select" className="text-sm">
                  {/* Pilih Dapur: */}
                </label>
                <select
                  id="kitchen-select"
                  value={selectedKitchenId}
                  onChange={(e) => setSelectedKitchenId(e.target.value)}
                  className="border rounded px-2 py-1"
                >
                  <option value="">Pilih Dapur</option>
                  {kitchens.map((kitchen) => (
                    <option key={kitchen.id} value={kitchen.id}>
                      {kitchen.kitchen_name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {/* Search Bar */}
            <div className="flex pb-4 rounded-[50px] flex-row w-full justify-start items-center gap-2">
              {tombol ? (
                <div className="flex items-center relative w-full">
                  <Input
                    type="text"
                    placeholder="Telusuri data berdasarkan nama bahan baku"
                    value={stockSearchQuery}
                    onChange={(e) => setStockSearchQuery(e.target.value)}
                    className="flex w-full rounded-full p-2 pl-10 bg-white text-gray-400 shadow-sm items-center justify-start"
                  />
                  <div className="flex items-center gap-2 absolute left-2">
                    <MagnificationLogo className="size-4" />
                    <VerticalLine />
                  </div>
                </div>
              ) : (
                <div className="flex py-4 rounded-[50px] flex-row justify-start items-center gap-2">
                  <div className="flex rounded-full p-2 bg-white text-gray-400 shadow-sm items-center justify-start">
                    <MagnificationLogo className="w-6 h-6" />
                    <VerticalLine className="mx-1" />
                    <input
                      type="text"
                      placeholder="Telusuri data berdasarkan nama bahan baku"
                      className="flex-1 bg-transparent outline-none border-none text-gray-800 px-2 focus:outline-none focus:ring-none focus:ring-transparent text-xxs md:text-xxs lg:text-base"
                      style={{
                        width: `${"Telusuri data berdasarkan nama bahan baku".length}ch`,
                      }}
                      value={stockSearchQuery} // Controlled input
                      onChange={(e) => setStockSearchQuery(e.target.value)} // Update search query
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
          <table
            className={`table-fixed mx-auto border-collapse text-left ${tombol ? "w-full" : "w-full"}`}
          >
            <thead>
              <tr className="bg-white">
                <th className="w-[10px] lg:w-[50px] border-b border-gray-300 px-4 py-2 rounded-tl-[15px] text-xxs md:text-xxs lg:text-base">
                  No.
                </th>
                {roleId === 4 && (
                  <th
                    className="border-b border-gray-300 px-4 py-2 cursor-pointer text-xxs md:text-xxs lg:text-base"
                    onClick={() => handleSort("kitchen_name")}
                  >
                    Dapur{" "}
                    <span className="float-right">
                      {getSortArrow("kitchen_name")}
                    </span>
                  </th>
                )}
                <th
                  className="border-b border-gray-300 px-4 py-2 cursor-pointer text-xxs md:text-xxs lg:text-base w-1/3"
                  onClick={() => handleSort("ingredient_name")}
                >
                  Bahan Baku{" "}
                  <span className="float-right">
                    {getSortArrow("ingredient_name")}
                  </span>
                </th>
                <th className="border-b border-gray-300 pe-10 py-2 text-xxs md:text-xxs lg:text-base rounded-tr-[15px] text-center">
                  Stok
                </th>
              </tr>
            </thead>
            <tbody className="overflow-x-auto">
              {paginatedData.map((item, index) => {
                const rawDisplay =
                  editedStocks[item.ingredient_id] !== undefined
                    ? editedStocks[item.ingredient_id]
                    : (initialStocks[item.ingredient_id] ?? formatInputValue(String(item.stock)));
                const isStockChanged =
                  editedStocks[item.ingredient_id] !== undefined &&
                  parseNumberFromInput(editedStocks[item.ingredient_id]) !==
                    parseNumberFromInput(initialStocks[item.ingredient_id] ?? String(item.stock));
                return (
                  <tr
                    key={item.ingredient_id}
                    className={`hover:bg-gray-200 overflow-x-auto ${
                      index % 2 === 0 ? "bg-gray-100" : "bg-white"
                    } border-b border-gray-300`}
                  >
                    <td className="w-[10px] lg:w-[50px] px-4 py-2 text-left text-xxs md:text-xxs lg:text-base">
                      {index + 1 + (currentPage - 1) * itemsPerPage}
                    </td>
                    {roleId === 4 && (
                      <td className="border-gray-300 px-4 py-2 text-xxs md:text-xxs lg:text-base">
                        {item.kitchen_name}
                      </td>
                    )}
                    <td className="border-gray-300 px-4 py-2 text-xxs md:text-xxs lg:text-base w-1/3">
                      {item.ingredient_name}
                    </td>
                    <td className="border-gray-300 w-full px-4 py-2 text-right text-xxs md:text-xxs lg:text-base flex items-center gap-2">
                      <div className="flex items-center justify-between gap-1 w-1/2">
                        {roleId === 3 ? (
                          <Input
                            type="text"
                            value={rawDisplay}
                            onChange={(e) => {
                              const formatted = formatInputValue(e.target.value);
                              handleStockChange(item.ingredient_id, formatted);
                            }}
                            className="border px-2 py-1 rounded text-right w-full text-xxs md:text-xxs lg:text-base text-right w-1/2"
                          />
                        ) : (
                          <div className="text-right w-full text-xxs md:text-xxs lg:text-base">
                            {formatNumber(item.stock)}
                          </div>
                        )}{" "}
                        &nbsp;
                        <div>
                          {item.uoq_code}
                        </div>
                      </div>
                      {roleId === 3 && isStockChanged && (
                        <button
                          onClick={() => handleSaveStock(item.ingredient_id, item)}
                          className="ml-2 px-3 py-1 bg-merah-no text-white rounded hover:bg-merah-hover"
                        >
                          Simpan
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {/* Pagination Controls */}
          <div className="flex justify-center items-center mt-4 text-xs sm:text-sm">
            <button
              className={`px-2 py-2 rounded ${
                currentPage === 1
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-gray-500 hover:text-black"
              }`}
              onClick={handlePreviousPage}
              disabled={currentPage === 1}
            >
              <span className="font-bold text-lg sm:text-2xl">{"<"}</span>
            </button>
            <div className="flex items-center overflow-x-auto">
              {pageNumbers.map((page, index) => (
                <span
                  key={index}
                  onClick={() =>
                    typeof page === "number" && setCurrentPage(page)
                  }
                  className={`mx-1 px-2 sm:px-3 py-1 cursor-pointer rounded-full ${
                    currentPage === page
                      ? "text-black font-semibold"
                      : "text-gray-700"
                  } ${page === "..." ? "cursor-default text-gray-400" : ""}`}
                >
                  {page}
                </span>
              ))}
            </div>
            <button
              className={`px-2 py-2 rounded ${
                currentPage === totalPages
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-gray-500 hover:text-black"
              }`}
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
            >
              <span className="font-bold text-lg sm:text-2xl">{">"}</span>
            </button>
          </div>
        </>
      )}
    </div>
  );
}
